/**
 * Aba "Pedidos" da ficha, como a ficha de Clientes do Legado: busca e filtro
 * por estado no topo, um cartão por pedido do cliente (o pedido desta ficha
 * aberto; os outros do mesmo cliente fechados) e, dentro de cada pedido, os
 * ambientes com a etapa atual da planilha e a leitura da IA. Cada ambiente
 * abre as pendências e decisões ligadas a ele.
 */
import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { FiChevronDown, FiSearch } from "react-icons/fi";

import { formatarDataLocal, plural } from "../../../utils/dinheiro";
import StatList from "../../Clientes/StatList";
import { BotaoDeTexto } from "../conversas/estilos";
import {
  ESTADO_DO_AMBIENTE,
  ORDEM_DOS_ESTADOS,
  chaveDoAmbiente,
  contarEstados,
  estadoDoAmbiente,
  ligadosAoAmbiente,
  textoAtraso,
  textoPrazo,
} from "../apresentacao";
import {
  AbrirLigados,
  AtrasoNoMeta,
  CabecalhoDaTabela,
  Celula,
  Chip,
  ChipRow,
  ClearLink,
  EmptyState,
  Ligados,
  LinhaDoAmbiente,
  LinhasDeAmbiente,
  MarcaDoPedido,
  MetaNoCelular,
  NomeDoAmbiente,
  PanelToolbar,
  PedidoBody,
  PedidoCard,
  PedidoContent,
  PedidoList,
  PedidoMeta,
  PedidoToggleButton,
  PedidoToggleIcon,
  SearchBox,
  SearchIcon,
  SearchInput,
  SeloDoEstado,
  SituacaoDaIA,
  StatusChip,
  StatusChipsRow,
  TabelaDeAmbientes,
  TituloDoPedido,
  ToolbarMeta,
} from "./estilos";

const TRANSICAO = { duration: 0.24, ease: [0.23, 1, 0.32, 1] };

/** "Esperando o cliente" → "esperando o cliente" (só a primeira letra muda). */
const minuscula = (texto) => texto.charAt(0).toLowerCase() + texto.slice(1);

function confere(ambiente, termo, estados) {
  if (estados.size && !estados.has(estadoDoAmbiente(ambiente))) return false;
  if (!termo) return true;
  const alvo = chaveDoAmbiente(
    [ambiente.ambiente, ambiente.etapa_rotulo, ambiente.projetista, ambiente.ia?.situacao].filter(Boolean).join(" "),
  );
  return alvo.includes(termo);
}

function textoDoPrazo(ambiente) {
  return ambiente.limite ? `Limite ${formatarDataLocal(ambiente.limite).slice(0, 5)}` : "Sem limite";
}

function Ligacoes({ pendencias, decisoes }) {
  return (
    <Ligados>
      {pendencias.length ? (
        <div>
          <h4>Pendências</h4>
          <ul>
            {pendencias.map((p) => (
              <li key={`p-${p.id}`}>
                {p.descricao}
                <small>
                  {[p.tipo_rotulo, textoPrazo(p.prazo), p.atribuida_a ? `com ${p.atribuida_a.nome}` : ""].filter(Boolean).join(" · ")}
                </small>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {decisoes.length ? (
        <div>
          <h4>Decisões</h4>
          <ul>
            {decisoes.map((d) => (
              <li key={`d-${d.id}`}>
                {d.descricao}
                {d.data ? <small>{formatarDataLocal(d.data)}</small> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Ligados>
  );
}

function Ambiente({ ambiente, ligados }) {
  const [aberto, setAberto] = useState(false);
  const estado = estadoDoAmbiente(ambiente);
  const leitura = estado ? ESTADO_DO_AMBIENTE[estado] : null;
  const atrasado = ambiente.atraso_dias > 0;
  const { pendencias, decisoes } = ligados;
  const total = pendencias.length + decisoes.length;
  const resumoLigados = [
    pendencias.length ? plural(pendencias.length, "pendência", "pendências") : "",
    decisoes.length ? plural(decisoes.length, "decisão", "decisões") : "",
  ].filter(Boolean).join(" · ");
  const etapa = `${ambiente.etapa_rotulo || "Sem etapa"}${ambiente.fora_de_ordem ? " (fora de ordem)" : ""}`;
  return (
    <li>
      <LinhaDoAmbiente>
        <NomeDoAmbiente>
          <b>{ambiente.ambiente}</b>
          {total ? (
            <AbrirLigados type="button" $aberto={aberto} aria-expanded={aberto} onClick={() => setAberto((v) => !v)}>
              {resumoLigados}
              <FiChevronDown size={13} aria-hidden="true" />
            </AbrirLigados>
          ) : null}
        </NomeDoAmbiente>
        <Celula className="amb-etapa">{etapa}</Celula>
        <Celula className="amb-ia">
          {leitura ? <SeloDoEstado $tom={leitura.tom}>{leitura.rotulo}</SeloDoEstado> : null}
          <SituacaoDaIA $vazia={!ambiente.ia?.situacao}>
            {ambiente.ia?.situacao || "Sem leitura da IA ainda"}
          </SituacaoDaIA>
        </Celula>
        <Celula className="amb-prazo" $tom={atrasado ? "danger" : undefined}>
          {textoDoPrazo(ambiente)}
          {atrasado ? <strong style={{ display: "block" }}>{textoAtraso(ambiente.atraso_dias)}</strong> : null}
        </Celula>
        <Celula className="amb-projetista">{ambiente.projetista || "Sem projetista"}</Celula>
        <MetaNoCelular $tom={atrasado ? "danger" : undefined}>
          {textoDoPrazo(ambiente)}
          {atrasado ? <> · <strong>{textoAtraso(ambiente.atraso_dias)}</strong></> : null}
          {` · ${ambiente.projetista || "Sem projetista"}`}
        </MetaNoCelular>
      </LinhaDoAmbiente>
      {aberto ? <Ligacoes pendencias={pendencias} decisoes={decisoes} /> : null}
    </li>
  );
}

function metaDoPedido(pedido, ambientes) {
  const contagens = contarEstados(pedido.ambientes);
  const esperandoEquipe = contagens.find((c) => c.estado === "esperando_equipe");
  return [
    plural((pedido.ambientes || []).length, "ambiente", "ambientes"),
    esperandoEquipe ? `${esperandoEquipe.total} esperando a equipe` : "",
    pedido.entrega_prevista ? `entrega ${formatarDataLocal(pedido.entrega_prevista)}` : "",
    ambientes !== null ? `${ambientes.length} no filtro` : "",
  ].filter(Boolean).join(" · ");
}

function CartaoDoPedido({ pedido, aberto, aoAlternar, ambientes, filtrando, ligacoes, aoAbrirFicha }) {
  const contagens = contarEstados(pedido.ambientes);
  const visiveis = filtrando ? ambientes : pedido.ambientes || [];
  const semResultado = filtrando && visiveis.length === 0;
  return (
    <PedidoCard
      id={`pedido-${pedido.numero_pedido}`}
      layout
      transition={TRANSICAO}
      style={{ opacity: semResultado ? 0.55 : 1 }}
      aria-label={`Pedido ${pedido.numero_pedido}`}
    >
      <PedidoToggleButton type="button" aria-expanded={aberto} onClick={() => aoAlternar(pedido.numero_pedido)}>
        <div>
          <TituloDoPedido>
            Pedido {pedido.numero_pedido}
            {pedido.atual ? <MarcaDoPedido>Esta ficha</MarcaDoPedido> : null}
          </TituloDoPedido>
          <PedidoMeta>
            {metaDoPedido(pedido, filtrando ? visiveis : null)}
            {pedido.atraso_dias > 0 ? <> · <AtrasoNoMeta>{textoAtraso(pedido.atraso_dias)}</AtrasoNoMeta></> : null}
          </PedidoMeta>
        </div>
        <PedidoToggleIcon $open={aberto}>
          <FiChevronDown size={18} aria-hidden="true" />
        </PedidoToggleIcon>
      </PedidoToggleButton>
      <AnimatePresence initial={false}>
        {aberto ? (
          <PedidoContent
            key="conteudo"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={TRANSICAO}
          >
            {contagens.length ? (
              <ChipRow>
                {contagens.map((c) => (
                  <Chip key={c.estado} $tone={c.tom === "neutral" ? "default" : c.tom}>
                    {c.total} {minuscula(c.rotulo)}
                  </Chip>
                ))}
              </ChipRow>
            ) : null}
            <PedidoBody>
              <StatList
                columns={2}
                groups={[
                  {
                    title: "Pedido",
                    items: [
                      { label: "Etapa atual", value: pedido.etapa_rotulo || "Sem etapa" },
                      { label: "Projetistas", value: (pedido.projetistas || []).join(", ") || "Sem projetista" },
                      { label: "Entrega prevista", value: pedido.entrega_prevista ? formatarDataLocal(pedido.entrega_prevista) : "Sem data" },
                    ],
                  },
                ]}
              />
              {pedido.atual ? null : (
                <div>
                  <BotaoDeTexto type="button" onClick={() => aoAbrirFicha(pedido.numero_pedido)}>
                    Abrir a ficha do pedido {pedido.numero_pedido}
                  </BotaoDeTexto>
                  <small style={{ marginLeft: 8, color: "var(--ink-3)" }}>com as pendências e conversas dele</small>
                </div>
              )}
              {semResultado ? (
                <EmptyState>Nenhum ambiente deste pedido confere com o filtro.</EmptyState>
              ) : (
                <TabelaDeAmbientes>
                  <CabecalhoDaTabela aria-hidden="true">
                    <span>Ambiente</span>
                    <span>Etapa</span>
                    <span>Leitura da IA</span>
                    <span>Prazo</span>
                    <span>Projetista</span>
                  </CabecalhoDaTabela>
                  <LinhasDeAmbiente aria-label={`Ambientes do pedido ${pedido.numero_pedido}`}>
                    {visiveis.map((ambiente) => (
                      <Ambiente
                        key={ambiente.chave || ambiente.ambiente}
                        ambiente={ambiente}
                        ligados={ligadosAoAmbiente(ambiente, pedido.atual ? ligacoes : undefined)}
                      />
                    ))}
                  </LinhasDeAmbiente>
                </TabelaDeAmbientes>
              )}
            </PedidoBody>
          </PedidoContent>
        ) : null}
      </AnimatePresence>
    </PedidoCard>
  );
}

/**
 * `pedidos`: o desta ficha primeiro (`atual: true`), depois os outros do mesmo
 * cliente. `ligacoes`: pendências abertas e decisões do pedido desta ficha
 * (ligadas ao ambiente pelo nome quando a API não as põe no ambiente).
 */
export default function PedidosDaFicha({ pedidos, ligacoes, abertos, aoAlternar, aoAbrirVarios, aoAbrirFicha }) {
  const [busca, setBusca] = useState("");
  const [estados, setEstados] = useState(() => new Set());
  const termo = chaveDoAmbiente(busca);
  const filtrando = Boolean(termo) || estados.size > 0;

  const filtrados = useMemo(() => {
    const mapa = new Map();
    pedidos.forEach((p) => mapa.set(p.numero_pedido, (p.ambientes || []).filter((a) => confere(a, termo, estados))));
    return mapa;
  }, [pedidos, termo, estados]);

  const todos = pedidos.flatMap((p) => p.ambientes || []);
  const contagens = contarEstados(todos);
  const visiveis = [...filtrados.values()].reduce((soma, lista) => soma + lista.length, 0);

  // Filtrar abre os pedidos que têm ambiente no filtro.
  useEffect(() => {
    if (!filtrando) return;
    const comResultado = pedidos.filter((p) => filtrados.get(p.numero_pedido)?.length).map((p) => p.numero_pedido);
    if (comResultado.length) aoAbrirVarios(comResultado);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termo, estados]);

  function alternarEstado(estado) {
    setEstados((atuais) => {
      const novos = new Set(atuais);
      if (novos.has(estado)) novos.delete(estado);
      else novos.add(estado);
      return novos;
    });
  }

  return (
    <>
      <PanelToolbar>
        <SearchBox>
          <SearchIcon>
            <FiSearch size={14} aria-hidden="true" />
          </SearchIcon>
          <SearchInput
            type="search"
            value={busca}
            onChange={(evento) => setBusca(evento.target.value)}
            placeholder="Ambiente, etapa, projetista"
            aria-label="Buscar ambiente nos pedidos do cliente"
          />
        </SearchBox>
        {contagens.length ? (
          <StatusChipsRow role="group" aria-label="Filtrar pela leitura da IA">
            {ORDEM_DOS_ESTADOS.filter((e) => contagens.some((c) => c.estado === e)).map((estado) => {
              const ativo = estados.has(estado);
              const { rotulo, tom } = ESTADO_DO_AMBIENTE[estado];
              return (
                <StatusChip
                  key={estado}
                  type="button"
                  $tone={tom === "neutral" ? "default" : tom}
                  $active={ativo}
                  aria-pressed={ativo}
                  onClick={() => alternarEstado(estado)}
                >
                  {rotulo}
                </StatusChip>
              );
            })}
          </StatusChipsRow>
        ) : null}
        <ToolbarMeta>
          {filtrando ? `${visiveis} de ${plural(todos.length, "ambiente", "ambientes")}` : plural(todos.length, "ambiente", "ambientes")}
          {filtrando ? (
            <>
              {" "}
              <ClearLink
                type="button"
                onClick={() => {
                  setBusca("");
                  setEstados(new Set());
                }}
              >
                limpar
              </ClearLink>
            </>
          ) : null}
        </ToolbarMeta>
      </PanelToolbar>
      {pedidos.length === 0 ? (
        <EmptyState>Nenhum pedido deste cliente no Gerenciador.</EmptyState>
      ) : (
        <PedidoList>
          {pedidos.map((pedido) => (
            <CartaoDoPedido
              key={pedido.numero_pedido}
              pedido={pedido}
              aberto={abertos.has(pedido.numero_pedido)}
              aoAlternar={aoAlternar}
              filtrando={filtrando}
              ambientes={filtrados.get(pedido.numero_pedido) || []}
              ligacoes={ligacoes}
              aoAbrirFicha={aoAbrirFicha}
            />
          ))}
        </PedidoList>
      )}
    </>
  );
}
